import ElakayImage from '../../components/images/ElakayImage';
// <SwiperCanvasSlide> -- renders ONE canvas slide (Swiper Studio
// composition): background (color / gradient / image / GIF / video +
// overlay), positioned elements (text, image / GIF, button, shape, video),
// per-device layout, animations and links.
//
// Used by <DynamicSwiper> on real pages AND by the Studio canvas, so the
// editor shows exactly what visitors get.
//
// Links: a slide-wide link is a separate full-size <a> UNDER the elements
// (never an <a> wrapping other links -- nested anchors are invalid HTML).
// Elements without their own link let clicks through to it; an element with
// its own link wins where it is.
import React, { memo, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toImageUrl } from "../../config";
import { elakayAutoText } from "../pageInside/dataElakay";
import {
  animationCss, backgroundCss, elementBoxCss, elementContentCss, isActiveAction, isInternalUrl,
  normalizeSlide, objectPositionCss, resolveElementBox, slideAccessibleName,
} from "./swiperCanvasModel";

const text = elakayAutoText;

// Internal path -> SPA navigation; external / new tab -> a real <a> with
// rel="noopener noreferrer" (no window.opener, no referrer leak).
export const ActionLink = ({ action, className, style, children, label, onNavigate, tabIndex, ...rest }) => {
  const newTab = action.target === "_blank";
  if (isInternalUrl(action.url) && !newTab) {
    return <Link className={className} style={style} to={action.url} onClick={onNavigate} aria-label={label} tabIndex={tabIndex} {...rest}>{children}</Link>;
  }
  return (
    <a
      className={className}
      style={style}
      href={action.url}
      target={newTab ? "_blank" : undefined}
      rel="noopener noreferrer"
      aria-label={label}
      tabIndex={tabIndex}
      {...rest}
    >
      {children}
      {newTab && <span className="dsw-visually-hidden">{text("h_dsw_opens_new_tab", " (opens in a new tab)")}</span>}
    </a>
  );
};

// Video: only fetched once its slide has been shown, playing only while the
// slide is the visible one (autoplay is always muted -- browser rule).
const CanvasVideo = ({ media, options, className, style, active, eager, alt }) => {
  const ref = useRef(null);
  const [armed, setArmed] = useState(Boolean(active || eager));
  useEffect(() => { if (active) setArmed(true); }, [active]);
  const autoplay = options?.autoplay !== false;
  useEffect(() => {
    const video = ref.current;
    if (!video || !armed) return;
    if (active && autoplay) {
      const attempt = video.play?.();
      if (attempt && typeof attempt.catch === "function") attempt.catch(() => undefined);
    } else if (!active) {
      video.pause?.();
    }
  }, [active, armed, autoplay]);
  // Free the network / decoder when the component goes away.
  useEffect(() => () => {
    const video = ref.current;
    if (video) {
      video.pause?.();
      video.removeAttribute("src");
      video.load?.();
    }
  }, []);
  return (
    <video
      ref={ref}
      className={className}
      style={style}
      src={armed ? toImageUrl(media?.url) : undefined}
      muted={autoplay ? true : options?.muted !== false}
      loop={options?.loop !== false}
      playsInline
      autoPlay={Boolean(active && autoplay)}
      controls={options?.controls === true}
      preload={armed ? "auto" : "none"}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : "true"}
    />
  );
};

const CanvasImage = ({ media, className, style, alt, priority, enabled = true }) => {
  const [failed, setFailed] = useState(false);
  const src = media?.previewSrc || toImageUrl(media?.url);
  useEffect(() => { setFailed(false); }, [src]);
  if (!enabled || !src || failed) return <span className={`${className} dsw-el-media-missing`} style={style} aria-hidden="true" />;
  // GIFs are drawn by the browser as-is: they keep animating.
  return (
    <ElakayImage
      preset="banner"
      className={className}
      style={style}
      image={{ url: src, mimeType: media?.mimeType }}
      alt={alt || ""}
      loading={priority === "high" ? "eager" : "lazy"}
      fetchPriority={priority === "high" ? "high" : "auto"}
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
};

const CanvasElement = ({ element, device, active, animate, eager, onNavigate, slideLinked, renderElement }) => {
  const box = resolveElementBox(element, device);
  if (!box.visible) return null;
  const outer = { ...elementBoxCss(box), zIndex: 2 };
  const content = elementContentCss(element, box);
  const anim = animate ? animationCss(element.animation) : null;
  const linked = isActiveAction(element.action);
  const interactive = linked || (element.type === "video" && element.video?.controls);
  // Without its own link an element lets the click through to the slide
  // link (if any) -- it must not swallow it.
  const passThrough = !interactive && slideLinked;
  const className = [
    "dsw-el",
    `dsw-el-${element.type}`,
    element.type === "shape" ? `dsw-shape-${element.style?.shape || "rect"}` : "",
    linked ? "dsw-el-linked" : "",
    passThrough ? "dsw-el-passthrough" : "",
    box.h === null ? "dsw-el-autoheight" : "",
  ].filter(Boolean).join(" ");

  let inner;
  if (element.type === "text") {
    inner = <div className="dsw-el-content dsw-el-text-body" style={content}>{element.text}</div>;
  } else if (element.type === "button") {
    inner = <span className="dsw-el-content dsw-el-button-body" style={content}>{element.text}</span>;
  } else if (element.type === "shape") {
    inner = <span className="dsw-el-content dsw-el-shape-body" style={content} aria-hidden="true" />;
  } else if (element.type === "image") {
    inner = <CanvasImage media={element.media} className="dsw-el-content dsw-el-media" style={content} alt={element.alt} priority={eager ? "high" : "normal"} enabled={active || eager} />;
  } else if (element.type === "video") {
    inner = <CanvasVideo media={element.media} options={element.video} className="dsw-el-content dsw-el-media" style={content} active={active} eager={eager} alt={element.alt} />;
  }

  const body = linked ? (
    <ActionLink
      action={element.action}
      className="dsw-el-link"
      label={element.type === "image" || element.type === "shape" ? (element.alt || element.name || undefined) : undefined}
      onNavigate={onNavigate}
    >
      {inner}
    </ActionLink>
  ) : inner;

  const node = (
    <div className={className} style={outer} data-element-id={element.id}>
      <div className="dsw-el-anim" style={anim || undefined}>{body}</div>
    </div>
  );
  return renderElement ? renderElement(element, node, box) : node;
};

const CanvasBackground = ({ background, active, eager }) => {
  const bg = background || {};
  const media = bg.media;
  const mediaStyle = { objectFit: bg.fit === "contain" ? "contain" : "cover", objectPosition: objectPositionCss(bg.position) };
  return (
    <>
      {bg.type === "image" && media && (
        <CanvasImage media={media} className="dsw-canvas-bg-media" style={mediaStyle} alt="" priority={eager ? "high" : "normal"} enabled={active || eager} />
      )}
      {bg.type === "video" && media && (
        <CanvasVideo media={media} options={{ autoplay: true, muted: true, loop: true, controls: false }} className="dsw-canvas-bg-media" style={mediaStyle} active={active} eager={eager} />
      )}
      {Number(bg.overlayOpacity) > 0 && (
        <span className="dsw-canvas-overlay" style={{ background: bg.overlayColor || "#000000", opacity: Number(bg.overlayOpacity) }} aria-hidden="true" />
      )}
    </>
  );
};

// active: this slide is the visible one (videos play, "slide" animations
// run). animate: false in reduced-motion / animations-disabled mode.
// renderElement: Studio hook to wrap each element with selection handles.
const SwiperCanvasSlide = ({
  slide: rawSlide, device = "desktop", active = true, animate = true, eager = false, onNavigate,
  renderElement, interactive = true, replayToken = 0,
}) => {
  const slide = normalizeSlide(rawSlide);
  if (!slide) return null;
  const slideLinked = interactive && isActiveAction(slide.action) && slide.action.wholeSlide;
  // "slide" animations only run on the visible slide; "load" ones once.
  const runAnimations = animate && active;
  const name = slideAccessibleName(slide);
  return (
    <div
      className={`dsw-canvas${active ? " is-active" : ""}`}
      style={backgroundCss(slide.background)}
      role={name && !slideLinked ? "group" : undefined}
      aria-label={name && !slideLinked ? name : undefined}
    >
      <CanvasBackground background={slide.background} active={active} eager={eager} />
      {slideLinked && (
        <ActionLink
          action={slide.action}
          className="dsw-canvas-hit"
          label={name || text("h_dsw_open_promotion", "Open this promotion")}
          onNavigate={onNavigate}
        />
      )}
      {/* Re-applying an element's `animation` restarts it, so animations
          replay each time the slide becomes visible without re-mounting
          (videos keep their buffer). replayToken (Studio "Replay") forces a
          remount. */}
      <div className="dsw-canvas-elements" key={`anim-${replayToken}`}>
        {slide.elements.map((element) => (
          element.hidden ? null : (
            <CanvasElement
              key={element.id}
              element={element}
              device={device}
              active={active}
              animate={animate && (element.animation?.trigger === "load" || runAnimations)}
              eager={eager}
              onNavigate={onNavigate}
              slideLinked={slideLinked}
              renderElement={renderElement}
            />
          )
        ))}
      </div>
    </div>
  );
};

export default memo(SwiperCanvasSlide);
